import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getTargetType } from './targetTypeMap'
import {
  adaptUnifiedItemForVertical,
  adaptUnifiedListParams,
  VerticalRowConfig,
} from './unifiedResponseAdapter'

/**
 * Restaurant (ChainsTable) flag-gated unified swap (FIR-6995).
 *
 * ChainsTable is the one opps table that does NOT use `DECommonTable` — it has a
 * bespoke component (legacy + reports V2) with elastic search, a product
 * selector, and a rich summary header (`total_ltv`, `places_count`,
 * `avg_*`) the unified summary does NOT provide. So instead of the generic
 * `useUnifiedTableActions` hook, this returns the pieces the bespoke component
 * needs to splice the unified data source in behind `enable_unified_opps_endpoint`:
 *
 *  - `enabled`: whether the unified path is active (the flag is on).
 *  - `getListAction()`: returns a bound list fetcher matching `getChainsListV2`'s
 *    `(params, signal) => Promise<{ results, count }>` shape (the returned
 *    closure takes `signal` per-call and forwards it to the API), with each row
 *    re-nested under `chain` (and `contact_company_id` / `overriden_name` set)
 *    so the existing chain columns, detail links, and row actions resolve.
 *  - `unifiedCount`: the unified summary count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the unified rows.
 *
 * The rich summary header fields are intentionally left to the component's
 * existing `?? 0` fallbacks when the flag is on (the unified summary endpoint
 * does not yet return them) — the header renders zeros for LTV/places rather
 * than crashing. Count is overridden so it stays consistent with the rows.
 */

// ChainsTable columns nest under `chain` and read `chain.id` for the
// `/chain/...` detail link; row actions key off `contact_company_id`.
const RESTAURANT_ROW_CONFIG: VerticalRowConfig = {
  nestKey: 'chain',
  companyIdAliases: ['contact_company_id'],
}

export function useUnifiedChainsTable(filterParams: Record<string, any>): {
  enabled: boolean
  getListAction: () =>
    | ((
        params: Record<string, any>,
        signal?: AbortSignal
      ) => Promise<{ results: any[]; count: number }>)
    | null
  unifiedCount: number | undefined
} {
  const api = useApiService()
  const { enableUnifiedOppsEndpoint } = useFeatureFlagService()
  const enabled = !!enableUnifiedOppsEndpoint

  const listAction = useMemo(() => {
    if (!enabled) return null
    const targetType = getTargetType('restaurant')
    return async (params: Record<string, any>, signal?: AbortSignal) => {
      const res = await api.getOpportunitiesList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedItemForVertical(
            item as Record<string, unknown>,
            RESTAURANT_ROW_CONFIG
          )
        ),
      } as { results: any[]; count: number }
    }
  }, [enabled, api])

  const { data: unifiedSummary } = useQuery({
    // Match the legacy `useGetChainReportsSummary` summary query, which uses the
    // default (finite, 0) staleTime so the count refetches after mutations.
    // Do NOT set `staleTime: Infinity` here: mutations invalidate the legacy
    // summary key, not this unified key, so an infinite staleTime would leave
    // the count stale until a hard reload.
    enabled,
    queryKey: ['unified-chains-summary', filterParams],
    queryFn: async ({ signal }) =>
      api.getOpportunitiesSummary(
        getTargetType('restaurant'),
        adaptUnifiedListParams(filterParams, getTargetType('restaurant')),
        signal
      ),
  })

  return {
    enabled,
    getListAction: () => listAction,
    unifiedCount: unifiedSummary?.count,
  }
}
